import Vue from 'vue'
import {
  reactive, getCurrentInstance, onUnmounted,
} from '@vue/composition-api'
import {
  backoffSteps, sleep, makeRangeKey, Canceler,
} from '@/utils/async'
import { enqueueAggregation, getAggregationStatus } from '@/api/v2/data-export/Aggregation'
import { handleApiError, ApiError } from '@/utils/errorHandler'

type JobStatus = 'queued' | 'in_progress' | 'completed' | 'failed' | null

interface JobMeta {
  createdAt: string | null
  updatedAt: string | null
}

interface EnsureOptions {
  timeoutMs?: number
  pollMaxMs?: number
  pollStartMs?: number
  pollFactor?: number
  pollMaxIntervalMs?: number
  forceReload?: boolean
}

const defaultEnsureOptions: Required<EnsureOptions> = {
  timeoutMs: 5 * 60_000, // 全体タイムアウト 5分
  pollMaxMs: 5 * 60_000, // ポーリング最大 5分
  pollStartMs: 1000, // 初期間隔
  pollFactor: 1.5,
  pollMaxIntervalMs: 60_000, // 最大間隔 60s
  forceReload: false,
}

export function useAggregationJobs() {
  const vm = getCurrentInstance()!.proxy

  // 状態ストア
  const statuses = reactive<Record<string, JobStatus>>({})
  const metas = reactive<Record<string, JobMeta>>({})
  const results = reactive<Record<string, any | null>>({})
  const loadings = reactive<Record<string, boolean>>({})

  // in-flight 重複防止（同じ id+range への多重 ensure を防ぐ）
  const inflight = new Map<string, Promise<any>>()

  // アンマウント時にキャンセル
  const pollCancelers = new Map<string, Canceler>()
  onUnmounted(() => {
    pollCancelers.forEach(cancel => cancel())
    pollCancelers.clear()
    inflight.clear()
  })

  const getStatus = async (id: number, range: [string, string]): Promise<void> => {
    const key = makeRangeKey(id, range)
    try {
      Vue.set(loadings, key, true)
      const res = await getAggregationStatus(id, {
        start_date: range[0],
        end_date: range[1],
      })
      const { status, result, meta } = res
      Vue.set(statuses, key, status as JobStatus)
      Vue.set(metas, key, { createdAt: meta.createdAt, updatedAt: meta.updatedAt })
      if (result) Vue.set(results, key, result)
    } catch (err) {
      const apiError = err as ApiError
      if (apiError.response?.status === 404) {
        Vue.set(statuses, key, null)
        Vue.set(results, key, null)
      } else {
        handleApiError(apiError, { $toast: vm.$toast })
      }
    } finally {
      Vue.set(loadings, key, false)
    }
  }

  const enqueue = async (id: number, range: [string, string]) => {
    const key = makeRangeKey(id, range)
    try {
      Vue.set(loadings, key, true)
      await enqueueAggregation(id, {
        start_date: range[0],
        end_date: range[1],
      })
      Vue.set(statuses, key, 'in_progress')
    } catch (err) {
      const apiError = err as ApiError
      if (apiError.response?.status === 404) {
        Vue.set(statuses, key, null)
      } else {
        handleApiError(apiError, { $toast: vm.$toast })
      }
      throw err
    } finally {
      Vue.set(loadings, key, false)
    }
  }

  /**
   * ensure: 結果が無ければ enqueue。完了までポーリングし、結果を返す。
   * - in-flight 重複防止
   * - キャンセル対応
   * - 指数バックオフ＋ジッター
   */
  const ensure = async (
    id: number,
    range: [string, string],
    options: EnsureOptions = {},
  ): Promise<any> => {
    const baseKey = makeRangeKey(id, range)
    const {
      timeoutMs,
      pollMaxMs,
      pollStartMs,
      pollFactor,
      pollMaxIntervalMs,
      forceReload = false,
    } = { ...defaultEnsureOptions, ...options }

    // ★ in-flight キーはモード込みで分離
    const inflightKey = forceReload ? `${baseKey}::force` : baseKey
    if (inflight.has(inflightKey)) return inflight.get(inflightKey)!

    const p = (async () => {
      if (forceReload) {
        // 再集計: 既存結果は使わない
        delete results[baseKey]
        statuses[baseKey] = null
        await enqueue(id, range)
      } else {
        // 従来フロー：まず最新状態を取得
        await getStatus(id, range)

        if (!results[baseKey] && (statuses[baseKey] === null || statuses[baseKey] === 'failed')) {
          await enqueue(id, range)
        }
        // 既存結果が既に完成していれば即返却
        if (results[baseKey] && statuses[baseKey] === 'completed') {
          return results[baseKey]
        }
      }

      // ポーリング
      const start = Date.now()
      let canceled = false
      const cancel = () => { canceled = true }
      pollCancelers.set(inflightKey, cancel)

      const steps = backoffSteps(pollStartMs, pollFactor, pollMaxIntervalMs)
      try {
        const poll = async (): Promise<any> => {
          if (canceled) throw new Error('Polling canceled')
          if (Date.now() - start > Math.min(timeoutMs, pollMaxMs)) {
            throw new Error('集計結果を待っている間にタイムアウトしました')
          }

          await getStatus(id, range)
          const st = statuses[baseKey]
          const has = Boolean(results[baseKey])

          if (st === 'completed' && has) return results[baseKey]
          if (st === 'failed') throw new Error(`集計(id: ${id}) 失敗`)
          if (st === null) await enqueue(id, range) // キューが空なら再度 enqueue

          const wait = steps.next().value as number
          await sleep(wait)

          return poll()
        }

        return await poll()
      } finally {
        pollCancelers.delete(inflightKey)
      }
    })()

    inflight.set(inflightKey, p)
    try {
      const res = await p

      return res
    } finally {
      inflight.delete(inflightKey)
    }
  }

  return {
    // state
    statuses,
    metas,
    results,
    loadings,
    // actions
    ensure,
    getStatus,
  }
}
